import React, { useState, useEffect } from "react";
import mediaService from "../../services/mediaService";

function MediaManager() {
  const [mediaList, setMediaList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [directory, setDirectory] = useState("general");
  const [selectedMedia, setSelectedMedia] = useState(null);

  const directories = [
    { value: "general", label: "General" },
    { value: "hero", label: "Hero Section" },
    { value: "programs", label: "Programs" },
    { value: "blog", label: "Blog" },
    { value: "services", label: "Services" },
  ];

  useEffect(() => {
    loadMediaList();
  }, [directory]);

  const loadMediaList = async () => {
    setLoading(true);
    try {
      const data = await mediaService.getMediaList(directory);
      setMediaList(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error loading media:", error);
      setMediaList([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      const result = await mediaService.uploadMultiple(files, directory);
      alert("Files uploaded successfully!");
      loadMediaList();
    } catch (error) {
      alert(`Upload failed: ${error.message}`);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleDelete = async (filePath) => {
    if (!window.confirm("Are you sure you want to delete this file?")) {
      return;
    }

    try {
      await mediaService.deleteFile(filePath);
      alert("File deleted successfully!");
      loadMediaList();
    } catch (error) {
      alert(`Delete failed: ${error.message}`);
    }
  };

  const copyToClipboard = (url) => {
    navigator.clipboard.writeText(url);
    alert("URL copied to clipboard!");
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Media Manager</h1>
        <p className="text-gray-600">Upload and manage media files</p>
      </div>

      {/* Directory Selection */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Directory
            </label>
            <select
              value={directory}
              onChange={(e) => setDirectory(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {directories.map((dir) => (
                <option key={dir.value} value={dir.value}>
                  {dir.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload Files
            </label>
            <div className="relative">
              <input
                type="file"
                multiple
                accept="image/*,.pdf,.doc,.docx"
                onChange={handleUpload}
                disabled={uploading}
                className="sr-only"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className={`block px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-center cursor-pointer hover:border-blue-500 transition ${
                  uploading ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <span>📁</span>
                  <span>
                    {uploading
                      ? "Uploading..."
                      : "Click to upload or drag files"}
                  </span>
                </div>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Media Grid */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-6">
          Media Files ({mediaList.length})
        </h2>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">Loading media...</p>
          </div>
        ) : mediaList.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p>No media files in this directory</p>
            <p className="text-sm mt-2">Upload files to get started</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {mediaList.map((media, index) => {
              const fullUrl = mediaService.getFileUrl(
                media.path || media.url || media.file_path,
              );
              const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(
                media.name || media.filename,
              );

              return (
                <div
                  key={index}
                  className="bg-gray-50 rounded-lg overflow-hidden border border-gray-200 hover:border-blue-500 transition"
                >
                  {/* Thumbnail */}
                  <div className="aspect-square bg-gray-200 flex items-center justify-center overflow-hidden">
                    {isImage ? (
                      <img
                        src={fullUrl}
                        alt={media.name || media.filename}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="text-4xl">📄</div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-4">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {media.name || media.filename}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {media.size ? `${(media.size / 1024).toFixed(2)} KB` : ""}
                    </p>

                    {/* Actions */}
                    <div className="flex gap-2 mt-4">
                      <button
                        onClick={() => copyToClipboard(fullUrl)}
                        className="flex-1 text-xs px-2 py-1 bg-blue-100 text-blue-600 rounded hover:bg-blue-200 transition"
                      >
                        Copy URL
                      </button>
                      <button
                        onClick={() =>
                          handleDelete(
                            media.path || media.url || media.file_path,
                          )
                        }
                        className="flex-1 text-xs px-2 py-1 bg-red-100 text-red-600 rounded hover:bg-red-200 transition"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default MediaManager;
